import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Gift, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Admin tab: list all wedding gift payments (paid + pending). */
const AdminGifts = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/gifts");
      setGifts(data.gifts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load gifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const paid = gifts.filter((g) => g.payment_status === "paid");
  const totalEur = paid
    .filter((g) => (g.currency || "eur") === "eur")
    .reduce((s, g) => s + Number(g.amount || 0), 0);

  return (
    <div data-testid="admin-gifts-panel" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="editorial-kicker">Wedding gifts</p>
          <h2 className="editorial-h2 text-2xl mt-2">
            {paid.length} paid ·{" "}
            <span className="tabular-nums">€{totalEur.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </h2>
        </div>
        <Button
          type="button"
          onClick={load}
          variant="outline"
          disabled={loading}
          data-testid="admin-gifts-refresh"
          className="h-10 rounded-lg"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          <span className="ml-2 text-[11px] tracking-[0.22em] uppercase">Refresh</span>
        </Button>
      </div>

      {loading && gifts.length === 0 ? (
        <div className="py-16 text-center text-foreground/50">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : gifts.length === 0 ? (
        <div className="py-16 text-center text-foreground/50 border border-dashed border-border rounded-xl">
          <Gift className="w-8 h-8 mx-auto text-foreground/30" />
          <p className="mt-4 font-serif-display italic text-lg">No gifts yet.</p>
          <p className="mt-1 text-xs">Card contributions will appear here as they arrive.</p>
        </div>
      ) : (
        <div className="overflow-x-auto surface-card">
          <table className="w-full text-sm" data-testid="admin-gifts-table">
            <thead className="border-b border-border">
              <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-foreground/50">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Private note</th>
              </tr>
            </thead>
            <tbody>
              {gifts.map((g, i) => {
                const meta = g.metadata || {};
                const date = g.updated_at || g.created_at || "";
                const isPaid = g.payment_status === "paid";
                return (
                  <tr
                    key={g.session_id || i}
                    className="border-b border-border/50 last:border-0 align-top"
                    data-testid={`admin-gift-row-${i}`}
                  >
                    <td className="px-4 py-3 text-foreground/70 whitespace-nowrap">
                      {date ? new Date(date).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-serif-display italic">
                        {meta.from_name || "Anonymous"}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums font-serif-display">
                      {(g.currency || "eur").toUpperCase() === "EUR" ? "€" : ""}
                      {Number(g.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {(g.currency || "eur").toUpperCase() !== "EUR" && (
                        <span className="text-foreground/50 text-xs ml-1">
                          {(g.currency || "eur").toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          "inline-block px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase " +
                          (isPaid
                            ? "bg-accent/15 text-accent"
                            : "bg-foreground/5 text-foreground/50")
                        }
                      >
                        {g.payment_status || g.status || "pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground/70 max-w-[380px]">
                      {meta.message ? (
                        <p className="whitespace-pre-wrap font-serif-display italic">
                          &ldquo;{meta.message}&rdquo;
                        </p>
                      ) : (
                        <span className="text-foreground/40 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminGifts;
